<script lang="ts">
  import { onMount } from 'svelte';
  import { admin, type GalleryItem, type ItemStatus, type Page, type Stats } from '../gallery/api';
  import { LOCALES, locale, setLocale, t } from '../i18n/index.svelte';

  let authed = $state<boolean | null>(null);
  let configured = $state(true);
  let password = $state('');
  let loginError = $state('');
  let status = $state<ItemStatus | 'all'>('pending');
  let page = $state(1);
  let data = $state<Page | null>(null);
  let stats = $state<Stats | null>(null);
  let galleryOn = $state(true);
  let galleryConfigured = $state(true);
  let selected = $state<Set<string>>(new Set());
  let error = $state('');
  let viewing = $state<GalleryItem | null>(null);

  onMount(async () => {
    try {
      const me = await admin.me();
      authed = me.authenticated;
      configured = me.configured;
      if (authed) await refresh();
    } catch (e) {
      authed = false;
      loginError = e instanceof Error ? e.message : String(e);
    }
  });

  async function login(e: SubmitEvent) {
    e.preventDefault();
    loginError = '';
    try {
      await admin.login(password);
      password = '';
      authed = true;
      await refresh();
    } catch (err) {
      loginError = err instanceof Error ? err.message : String(err);
    }
  }

  async function logout() {
    await admin.logout();
    authed = false;
  }

  async function refresh() {
    error = '';
    try {
      const [q, s] = await Promise.all([admin.queue(status, page), admin.stats()]);
      data = q;
      stats = s.stats;
      galleryOn = s.gallery_enabled;
      galleryConfigured = s.gallery_configured;
      selected = new Set([...selected].filter((id) => q.items.some((i) => i.id === id)));
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      if (e instanceof Error && 'status' in e && (e as { status: number }).status === 401) authed = false;
    }
  }

  $effect(() => {
    void status;
    void page;
    if (authed) refresh();
  });

  async function act(id: string, a: 'approve' | 'reject' | 'hide' | 'delete') {
    if (a === 'delete' && !confirm(t('admin.confirmDelete'))) return;
    await (a === 'delete' ? admin.remove(id) : admin.action(id, a));
    await refresh();
  }

  async function bulk(a: 'approve' | 'reject' | 'hide' | 'delete') {
    if (!selected.size) return;
    if (a === 'delete' && !confirm(t('admin.confirmDeleteMany', { n: selected.size }))) return;
    await admin.bulk([...selected], a);
    selected = new Set();
    await refresh();
  }

  async function rename(item: GalleryItem, title: string) {
    if (title === item.title) return;
    await admin.edit(item.id, title);
    await refresh();
  }

  async function toggleGallery() {
    const r = await admin.setGallery(!galleryOn);
    galleryOn = r.gallery_enabled;
  }

  function toggle(id: string) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    selected = s;
  }

  const tabs: (ItemStatus | 'all')[] = ['pending', 'approved', 'hidden', 'rejected', 'all'];
  const fmtDate = (s: number) => new Date(s * 1000).toLocaleString(locale());
</script>

<div class="admin">
  <header class="row">
    <strong>{t('admin.title')}</strong>
    <span class="grow"></span>
    <div class="seg lang">
      {#each LOCALES as l (l.id)}
        <button class:on={locale() === l.id} onclick={() => setLocale(l.id)}>{l.label}</button>
      {/each}
    </div>
    <a href="/" class="muted small">{t('admin.toSite')}</a>
    {#if authed}<button onclick={logout} data-testid="admin-logout">{t('admin.logout')}</button>{/if}
  </header>

  {#if authed === false}
    <form class="login" onsubmit={login}>
      <h2>{t('admin.login')}</h2>
      {#if !configured}<p class="error">{t('admin.notConfigured')}</p>{/if}
      <input
        type="password"
        autocomplete="current-password"
        placeholder={t('admin.password')}
        bind:value={password}
        data-testid="admin-password"
      />
      <button class="primary" type="submit" data-testid="admin-login">{t('admin.signIn')}</button>
      {#if loginError}<p class="error small" data-testid="admin-login-error">{loginError}</p>{/if}
    </form>
  {:else if authed}
    <div class="top row">
      {#if stats}
        <span class="stat"><b>{stats.total}</b> {t('admin.stat.total')}</span>
        <span class="stat warn"><b data-testid="admin-pending-count">{stats.pending}</b> {t('admin.stat.pending')}</span
        >
        <span class="stat"><b>{stats.approved}</b> {t('admin.stat.approved')}</span>
      {/if}
      <span class="grow"></span>
      <label class="switch" title={galleryConfigured ? '' : t('admin.galleryDisabledByConfig')}>
        <input
          type="checkbox"
          checked={galleryOn}
          disabled={!galleryConfigured}
          onchange={toggleGallery}
          data-testid="admin-gallery-toggle"
        />
        {t('admin.galleryEnabled')}
      </label>
    </div>
    <div class="row tabsrow">
      <div class="seg">
        {#each tabs as s (s)}
          <button class:on={status === s} onclick={() => ((status = s), (page = 1))} data-testid={`admin-tab-${s}`}
            >{t(`admin.status.${s}`)}</button
          >
        {/each}
      </div>
      <span class="grow"></span>
      {#if selected.size}
        <span class="small">{t('admin.selected', { n: selected.size })}</span>
        <button onclick={() => bulk('approve')} data-testid="admin-bulk-approve">{t('admin.approve')}</button>
        <button onclick={() => bulk('hide')}>{t('admin.hide')}</button>
        <button onclick={() => bulk('reject')}>{t('admin.reject')}</button>
        <button class="danger" onclick={() => bulk('delete')}>{t('common.remove')}</button>
      {/if}
    </div>
    {#if error}<p class="error">{error}</p>{/if}
    {#if data}
      {#if !data.items.length}
        <p class="muted">{t('admin.empty')}</p>
      {/if}
      <div class="list">
        {#each data.items as item (item.id)}
          <div class="item" class:sel={selected.has(item.id)} data-testid="admin-item" data-title={item.title}>
            <input
              type="checkbox"
              checked={selected.has(item.id)}
              onchange={() => toggle(item.id)}
              aria-label={t('admin.select')}
            />
            <button class="thumb" onclick={() => (viewing = item)}
              ><img src={item.preview_url} alt="" loading="lazy" /></button
            >
            <div class="info">
              <input
                class="name"
                type="text"
                value={item.title}
                placeholder={t('gallery.untitled')}
                onchange={(e) => rename(item, (e.currentTarget as HTMLInputElement).value)}
              />
              {#if item.caption}<p class="muted small">{item.caption}</p>{/if}
              <p class="muted small mono">
                {t(`admin.status.${item.status ?? 'pending'}`)} · {item.kind} · {item.mime} · {(
                  item.bytes / 1e6
                ).toFixed(1)} MB
                {#if item.width}· {item.width}×{item.height}{/if} · {fmtDate(item.created_at)} · {item.ip_hash}
                {#if item.has_params}· {t('admin.hasParams')}{/if}
              </p>
            </div>
            <div class="actions">
              {#if item.status !== 'approved'}<button
                  onclick={() => act(item.id, 'approve')}
                  data-testid="admin-approve">{t('admin.approve')}</button
                >{/if}
              {#if item.status !== 'hidden'}<button onclick={() => act(item.id, 'hide')}>{t('admin.hide')}</button>{/if}
              {#if item.status !== 'rejected'}<button onclick={() => act(item.id, 'reject')}>{t('admin.reject')}</button
                >{/if}
              <button class="danger" onclick={() => act(item.id, 'delete')}>{t('common.remove')}</button>
            </div>
          </div>
        {/each}
      </div>
      {#if data.pages > 1}
        <div class="row pager">
          <button disabled={page <= 1} onclick={() => page--}>←</button>
          <span class="mono small">{page} / {data.pages}</span>
          <button disabled={page >= data.pages} onclick={() => page++}>→</button>
        </div>
      {/if}
    {/if}
  {/if}
</div>

{#if viewing}
  <div class="backdrop" role="presentation" onclick={() => (viewing = null)}>
    {#if viewing.mime.startsWith('video/')}
      <!-- svelte-ignore a11y_media_has_caption -->
      <video src={viewing.file_url} autoplay loop muted controls></video>
    {:else}
      <img src={viewing.file_url} alt={viewing.title} />
    {/if}
  </div>
{/if}

<style>
  .admin {
    max-width: 1100px;
    margin: 0 auto;
    padding: 16px 20px 40px;
    height: 100%;
    overflow-y: auto;
  }
  header {
    padding-bottom: 12px;
    border-bottom: 1px solid var(--line);
    margin-bottom: 16px;
    gap: 14px;
  }
  .grow {
    flex: 1;
  }
  .login {
    max-width: 320px;
    margin: 60px auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .top {
    gap: 18px;
    margin-bottom: 12px;
  }
  .stat b {
    font-size: 20px;
  }
  .stat.warn b {
    color: var(--accent);
  }
  .switch {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .tabsrow {
    margin-bottom: 12px;
    gap: 8px;
  }
  .list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .item {
    display: grid;
    grid-template-columns: auto 120px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 8px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--panel);
  }
  .item.sel {
    border-color: var(--accent);
  }
  .thumb {
    padding: 0;
    border: 0;
    width: 120px;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    border-radius: 6px;
    background: #000;
  }
  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .info {
    min-width: 0;
  }
  .name {
    font-weight: 600;
    padding: 3px 6px;
  }
  .info p {
    margin: 4px 0 0;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
    max-width: 260px;
  }
  .actions button {
    font-size: 12px;
    padding: 4px 8px;
  }
  .danger:hover {
    border-color: var(--danger);
    color: var(--danger);
  }
  .small {
    font-size: 12px;
  }
  .pager {
    justify-content: center;
    margin-top: 16px;
  }
  .backdrop {
    position: fixed;
    inset: 0;
    background: #000d;
    display: grid;
    place-items: center;
    z-index: 40;
    padding: 20px;
  }
  .backdrop img,
  .backdrop video {
    max-width: 100%;
    max-height: 90vh;
  }
  .lang button {
    padding: 3px 8px;
    font-size: 12px;
  }
  @media (max-width: 720px) {
    .item {
      grid-template-columns: auto 80px 1fr;
    }
    .actions {
      grid-column: 1 / -1;
      max-width: none;
    }
  }
</style>
